/** @jsxRuntime classic */
import "react-app-polyfill/ie11";
import "react-app-polyfill/stable";

import React, { Suspense } from "react";
import ReactDOM from "react-dom";
import { Router } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import Promise from "bluebird";
import { QueryClient, QueryClientProvider } from "react-query";
import { RecoilRoot } from "recoil";
import "moment-duration-format";

import { App } from "./App";
import i18n from "./config/i18n";
import history from "./config/history";
import { NotificationContainer } from "react-notifications";
import { UserContextProvider } from "./hooks";

import "react-notifications/lib/notifications.css";
//import "font-awesome/css/font-awesome.min.css";
import "react-select-plus/dist/react-select-plus.min.css";
import "react-select/dist/react-select.min.css";
import "react-datepicker/dist/react-datepicker.css";
import "rc-time-picker/assets/index.css";

import './assets/scss/style.css';
import './assets/scss/fix-style.css'

import "./scss/index.scss";
import "./scss/bootstrap_custom.scss";

import {
  AppLevelErrorBoundary,
  TopLevelErrorBoundary,
} from "./components/errorBoundaries";
import { ApiUnhandledListener } from "./components/ApiUnhandledListener";
import {GoogleReCaptchaProvider} from "react-google-recaptcha-v3";
import MaintenancePage from "./components/MaintenancePage";
import { useMaintenanceWindow } from "./utils/maintenanceWindow";

global.Promise = Promise;
Promise.cancel = undefined;

Promise.config({
  warnings: false,
  longStackTraces: false,
  cancellation: false,
  monitoring: false,
  asyncHooks: false,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: false,
      structuralSharing: false,
      useErrorBoundary: true,
    },
    mutations: {
      retry: false,
      useErrorBoundary: true,
    },
  },
});

const Root = () => {
  // Gated as early as possible, before any router/context/query provider
  // mounts, so no API calls or auth checks fire during the maintenance window.
  const inMaintenanceWindow = useMaintenanceWindow();

  if (inMaintenanceWindow) {
    return <MaintenancePage />;
  }

  return (
    <TopLevelErrorBoundary>
      <NotificationContainer />
      <RecoilRoot>
        <Suspense fallback={null}>
          <I18nextProvider i18n={i18n}>
            <ApiUnhandledListener />
            <QueryClientProvider client={queryClient}>
              <Router history={history}>
                <AppLevelErrorBoundary>
                  <UserContextProvider>
                    <AppLevelErrorBoundary>
                      <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}>
                        <App />
                      </GoogleReCaptchaProvider>
                    </AppLevelErrorBoundary>
                  </UserContextProvider>
                </AppLevelErrorBoundary>
              </Router>
            </QueryClientProvider>
          </I18nextProvider>
        </Suspense>
      </RecoilRoot>
    </TopLevelErrorBoundary>
  );
};

ReactDOM.render(<Root />, document.getElementById("root"));
